import { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import history from '../../../history';
import * as paths from '../../../routes/paths';
import * as D from '../../../clients/mira/types/Device';
import Button from '@raydiant/raydial/components/Button';
import Dialog, {
  DialogHeader,
  DialogHeading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import ComboBox, { ComboBoxItem } from '@raydiant/raydial/components/ComboBox';
import Pill from '@raydiant/raydial/components/Pill';
import { User } from '@raydiant/raydial/components/Icons';
import { LocationACL, ResourceGrant } from '@raydiant/api-client-js';
import UsersWithAccess from './UsersWithAccess';
import { DEVICE_GRANT_SELECT_OPTIONS } from './DeviceGrantSelect';
import {
  getLocationACLsToDevice,
  getResourceACLToLocationACL,
  selectItemToGrants,
} from '../utils';
import { useToastState } from '@raydiant/raydial/components/Toast';
import { selectUserProfile } from '../../../selectors/user';
import useCreateResourceACLV5 from '../../../hooks/useCreateResourceACLV5';
import useUpdateResourceACLV5 from '../../../hooks/useUpdateResourceACLV5';
import useDeleteResourceACLV5 from '../../../hooks/useDeleteResourceACLV5';
import * as deviceActions from '../../../actions/devices';
import { selectDomainProfiles } from '../../../selectors/v2/domains';
import { changeV3DomainProfilesToV4 } from '../../LibraryPage/utils';

export const BACK_BUTTON_ICON = (
  <svg
    width="22"
    height="19"
    viewBox="0 0 22 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.42188 8.46875L8.92188 0.96875C9.48438 0.359375 10.4688 0.359375 11.0312 0.96875C11.6406 1.53125 11.6406 2.51562 11.0312 3.07812L6.10938 8H20.5C21.2969 8 22 8.70312 22 9.5C22 10.3438 21.2969 11 20.5 11H6.10938L11.0312 15.9688C11.6406 16.5312 11.6406 17.5156 11.0312 18.0781C10.4688 18.6875 9.48438 18.6875 8.92188 18.0781L1.42188 10.5781C0.8125 10.0156 0.8125 9.03125 1.42188 8.46875Z"
      fill="black"
    />
  </svg>
);

interface DeviceShareProps {
  isOpen: boolean;
  device: D.Device;
}

const DeviceShare = ({ isOpen, device }: DeviceShareProps) => {
  const toastState = useToastState();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectUserProfile);
  const domainProfiles = useSelector(selectDomainProfiles);

  const createResourceACL = useCreateResourceACLV5();
  const updateResourceACL = useUpdateResourceACLV5();
  const deleteResourceACL = useDeleteResourceACLV5();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<ResourceGrant>('read');
  const [shareWithDomain, setShareWithDomain] = useState(false);

  const v4DomainProfiles = useMemo(
    () => changeV3DomainProfilesToV4(domainProfiles),
    [domainProfiles],
  );

  const sortedDomainUsers = useMemo(
    () => [...v4DomainProfiles].sort((a, b) => a.name.localeCompare(b.name)),
    [v4DomainProfiles],
  );

  const selectedUsers = useMemo(
    () => sortedDomainUsers.filter((user) => selectedUserIds.includes(user.id)),
    [sortedDomainUsers, selectedUserIds],
  );

  const updateDevice = useCallback(
    (updatedDevice: Partial<D.Device>) => {
      if (!device) return;
      dispatch(
        deviceActions.updateDevice(
          {
            ...device,
            ...updatedDevice,
          },
          {},
        ),
      );
    },
    [dispatch, device],
  );

  const handleSelectUser = useCallback((value: string) => {
    if (value === 'domain') {
      setShareWithDomain(true);
      return;
    }
    setSelectedUserIds((prev) => {
      if (!prev.includes(value)) {
        return [...prev, value];
      }
      return prev;
    });
  }, []);

  const handleUnselectUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  }, []);

  const handleReset = useCallback(() => {
    setSelectedUserIds([]);
    setSelectedGrant('read');
    setShareWithDomain(false);
  }, []);

  const handleShareContent = useCallback(async () => {
    try {
      const result = await createResourceACL.mutateAsync({
        params: {
          resourceId: device.resource.id,
          grantProfileIds: !shareWithDomain ? selectedUserIds : [],
          grantDomainId: shareWithDomain ? currentUser?.domainId : '',
          grants: selectItemToGrants(selectedGrant),
        },
      });
      updateDevice(getLocationACLsToDevice(device, result));
      handleReset();
      toastState.add({
        title: 'Device Shared',
        description: `${device.name} shared successfully`,
      });
    } catch (error) {
      toastState.add({
        title: 'Could not share device',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  }, [
    createResourceACL,
    device,
    currentUser,
    selectedUserIds,
    shareWithDomain,
    selectedGrant,
    updateDevice,
    toastState,
    handleReset,
  ]);

  const handleUpdateGrant = useCallback(
    async (acl: LocationACL, grant: ResourceGrant) => {
      try {
        const acls = await updateResourceACL.mutateAsync({
          params: {
            resourceId: device.resource.id,
            grantProfileIds: acl.grantProfile.id ? [acl.grantProfile.id] : [],
            grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
            grants: selectItemToGrants(grant),
          },
        });

        updateDevice(getLocationACLsToDevice(device, acls));
        handleReset();
        toastState.add({
          title: 'Grant Updated',
          description: 'Grant updated successfully',
        });
      } catch (error) {
        toastState.add({
          title: 'Could not update grant',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [updateResourceACL, device, updateDevice, handleReset, toastState],
  );

  const handleUnShareContent = useCallback(
    async (acl: LocationACL) => {
      try {
        await deleteResourceACL.mutate({
          resourceId: device.resource.id,
          params: {
            grantProfileIds: acl.grantProfile?.id ? [acl.grantProfile.id] : [],
            grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
          },
        });
        const updatedResourceACLs = device.resource.r.resourceACLs.filter(
          (item) =>
            item.id !== acl.id || item.grantDomainId !== acl.grantDomainId,
        );
        updateDevice({
          ...device,
          resource: {
            ...device.resource,
            r: {
              ...device.resource.r,
              resourceACLs: updatedResourceACLs,
            },
          },
        });
        handleReset();
        toastState.add({
          title: 'User Access Removed',
          description: 'Access removed successfully',
        });
      } catch (error) {
        toastState.add({
          title: 'Could not remove user access',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [deleteResourceACL, device, updateDevice, handleReset, toastState],
  );

  const handleCancel = useCallback(() => {
    history.push(paths.screens.pattern);
  }, []);

  const validUserList = sortedDomainUsers.filter(
    (user) =>
      user.id &&
      !selectedUserIds.includes(user.id) &&
      !device.resource.r.resourceACLs.some(
        (acl) => acl.grantProfileId === user.id,
      ) &&
      user.id !== device.resource.profile.id,
  );

  const isContentSharedWithDomain = device.resource.r.resourceACLs.some(
    (acl) => acl.grantDomainId,
  );
  const showSelectedUsers = shareWithDomain || selectedUserIds.length > 0;

  const renderSelectedUsers = () => (
    <div className="flex gap-2 flex-wrap pt-2">
      {shareWithDomain && (
        <Pill
          className="text-lg"
          icon={<User size={16} />}
          showCloseIcon
          onClick={() => setShareWithDomain(false)}
          label="Everyone at domain"
        />
      )}
      {!shareWithDomain &&
        selectedUsers.map((user) => (
          <Pill
            key={user.id}
            className="text-lg"
            icon={<User size={16} />}
            showCloseIcon
            onClick={() => handleUnselectUser(user.id)}
            label={user.name}
          />
        ))}
    </div>
  );

  if (!currentUser) return null;

  return (
    <Modal isOpen={isOpen} className="text-black overflow-auto">
      <Dialog showCloseButton onClose={handleCancel}>
        <DialogHeader>
          <div className="flex gap-4 items-start">
            {showSelectedUsers && (
              <Button
                variant="unstyled"
                onPress={handleCancel}
                className="mt-1"
              >
                {BACK_BUTTON_ICON}
              </Button>
            )}
            <div>
              <DialogHeading>
                <span>Share '{device.name}' device</span>
              </DialogHeading>
            </div>
          </div>
        </DialogHeader>

        <div>
          <div className="flex flex-col">
            <div className="pt-4 pb-6">
              <ComboBox
                onSelectionChange={(key) => handleSelectUser(key as string)}
                selectedKey={''}
                placeholder="Select users"
                isDisabled={shareWithDomain || validUserList.length === 0}
              >
                {!isContentSharedWithDomain && (
                  <ComboBoxItem id="domain" key="domain" textValue="domain">
                    Everyone at domain
                  </ComboBoxItem>
                )}
                {validUserList.map((user) => (
                  <ComboBoxItem
                    key={user.id}
                    id={user.id}
                    textValue={user.name}
                  >
                    {user.name}
                  </ComboBoxItem>
                ))}
              </ComboBox>
              {renderSelectedUsers()}
            </div>

            {!showSelectedUsers ? (
              <div>
                <UsersWithAccess
                  owner={device.resource.profile}
                  contentType="device"
                  grantOptions={DEVICE_GRANT_SELECT_OPTIONS}
                  locationACLs={device.resource.r.resourceACLs.map(
                    (resourceACL) => getResourceACLToLocationACL(resourceACL),
                  )}
                  domainUsers={sortedDomainUsers}
                  onRemove={handleUnShareContent}
                  onUpdate={handleUpdateGrant}
                />
                <div className="mt-8 md:px-6 pb-4">
                  <Button onPress={handleCancel} variant="primary" fullWidth>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <ComboBox
                  isReadOnly
                  label="Access Level"
                  onSelectionChange={(key) =>
                    setSelectedGrant(key as ResourceGrant)
                  }
                  selectedKey={selectedGrant}
                >
                  {DEVICE_GRANT_SELECT_OPTIONS.map((grant) => (
                    <ComboBoxItem
                      key={grant.id}
                      id={grant.id}
                      textValue={grant.title}
                      checkMarkSelected
                    >
                      <div className="cursor-pointer">
                        <div>{grant.title}</div>
                        <div className="text-sm text-gray-600">
                          {grant.description}
                        </div>
                      </div>
                    </ComboBoxItem>
                  ))}
                </ComboBox>
                <div className="mt-8 md:px-4 flex pb-4 gap-6">
                  <Button onPress={handleCancel} variant="secondary" fullWidth>
                    Cancel
                  </Button>
                  <Button
                    isDisabled={!showSelectedUsers}
                    onPress={handleShareContent}
                    variant="primary"
                    fullWidth
                  >
                    Share Now
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </Modal>
  );
};

export default DeviceShare;
